// @ts-strict-ignore
import { InspirationProductsProductCard } from './ProductCard'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Button, Container, Image } from '@components/gassan-ui'
import { Heading } from '@components/gassan-ui/Typography/Heading'
import { Text } from '@components/gassan-ui/Typography/Text'
import { Article, useArticlesByIdQuery } from '@generated'
import { cn } from '@lib/cn'
import { getAspectRatioImage } from '@lib/get-image-aspect-ratio'
import { getKontentImage } from '@lib/kontent'
import { CbInspirationProducts } from '@lib/kontent/models/CbInspirationProducts'
import { CbSingleInspirationImage } from '@lib/kontent/models/CbSingleInspirationImage'
import { CbSingleInspirationProductsProduct } from '@lib/kontent/models/CbSingleInspirationProductsProduct'
import { FC, useEffect, useState } from 'react'

type InspirationProductsProps = CbInspirationProducts & {
  items: (CbSingleInspirationImage | CbSingleInspirationProductsProduct)[]
}

const InspirationProducts: FC<InspirationProductsProps> = ({
  title,
  description,
  items,
  featured_image,
  element_order,
  view_more_text__optional_,
  view_more_link__optional_,
}) => {
  const firstFourItems = items.slice(0, 4)
  const imageLeft = element_order.value[0].codename === 'image_left___products_right'
  const [articles, setArticles] = useState<Article[] | null>(null)
  const productIds = firstFourItems
    .map((item: any) => item.product_id && item.product_id.value)
    .filter((i) => i)

  const [result] = useArticlesByIdQuery({
    variables: { ids: productIds },
  })

  useEffect(() => {
    if (result.data && result.data.articlesById) {
      let products: Article[] = []

      for (const id of productIds) {
        const product = result.data.articlesById.find((item) => item.id === id)
        if (product) {
          products.push(product)
        }
      }
      setArticles(products)
    }
  }, [result]) // eslint-disable-line

  return (
    <Container
      className={cn('mb-8 grid grid-cols-1 gap-6', 'sm:mb-16', 'lg:mb-20 lg:grid-cols-2 lg:gap-8')}
      data-image-left={imageLeft ? '' : undefined}
    >
      <div
        className={cn(
          'self-start',
          'lg:sticky lg:top-8',
          'border-8',
          imageLeft ? 'order-1 lg:order-1' : 'order-1 lg:order-2',
        )}
      >
        <Image
          style={{ aspectRatio: `${getAspectRatioImage(featured_image)}` }}
          sources={[
            getKontentImage(featured_image.value[0].url, { width: 768 }),
            getKontentImage(featured_image.value[0].url, { width: 1024 }),
            getKontentImage(featured_image.value[0].url, { width: 1200 }),
          ]}
          alt="Product image"
        />
      </div>
      <div className={imageLeft ? 'order-2 lg:order-2' : 'order-2 lg:order-1'}>
        <div className="w-full max-w-[700px]">
          <Heading variant="h1" as="h2" className="mb-0">
            {title.value}
          </Heading>
          <Text className="mb-8">{description.value}</Text>
        </div>
        <div className={cn('grid grid-cols-2 gap-4', 'lg:gap-6')}>
          {firstFourItems.map((item: any, key) =>
            item.product_id ? (
              <InspirationProductsProductCard
                key={key}
                data={articles && articles.find((article) => article.id === item.product_id.value)}
              />
            ) : (
              <Image
                key={key}
                className="aspect-[2/3]"
                sources={[
                  getKontentImage(item.image.value[0].url, { width: 768 }),
                  getKontentImage(item.image.value[0].url, { width: 1024 }),
                  getKontentImage(item.image.value[0].url, { width: 1200 }),
                ]}
                alt="Product image"
              />
            ),
          )}
        </div>
        {view_more_text__optional_.value && view_more_link__optional_.value && (
          <div className={cn('mt-6 flex w-full justify-center', 'lg:mt-8')}>
            <LocalizedLink href={view_more_link__optional_.value}>
              <Button as="link" variant="dark">
                {view_more_text__optional_.value}
              </Button>
            </LocalizedLink>
          </div>
        )}
      </div>
    </Container>
  )
}

export default InspirationProducts
